
import type { ReactNode } from 'react';
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuthState, useAuthActions } from './AuthContext';
import { useGlobalWebSocket } from './GlobalWebSocketContext';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from '@/hooks/use-toast';

// Separate interface for balance state (changes frequently)
interface WalletBalanceContextType {
  balance: number;
  isLoading: boolean;
}

// Separate interface for actions (never changes)
interface WalletActionsContextType {
  refreshBalance: () => Promise<void>;
}

// Combined interface for backwards compatibility
interface WalletContextType extends WalletBalanceContextType, WalletActionsContextType {}

// Separate contexts
const WalletBalanceContext = createContext<WalletBalanceContextType | undefined>(undefined);
const WalletActionsContext = createContext<WalletActionsContextType | undefined>(undefined);

// Hook for balance only - use this when you only need balance/isLoading
export const useWalletBalance = (): WalletBalanceContextType => {
  const context = useContext(WalletBalanceContext);
  if (!context) {
    throw new Error('useWalletBalance must be used within a WalletProvider');
  }
  return context;
};

// Hook for actions only - use this when you only need refreshBalance
export const useWalletActions = (): WalletActionsContextType => {
  const context = useContext(WalletActionsContext);
  if (!context) {
    throw new Error('useWalletActions must be used within a WalletProvider');
  }
  return context;
};

// Combined hook for backwards compatibility
export const useWallet = (): WalletContextType => {
  const balance = useWalletBalance();
  const actions = useWalletActions();
  return useMemo(() => ({ ...balance, ...actions }), [balance, actions]);
};

interface WalletProviderProps {
  children: ReactNode;
}

export const WalletProvider: React.FC<WalletProviderProps> = ({ children }) => {
  const { user, isAuthenticated } = useAuthState();
  const { updateUserBalance } = useAuthActions();
  const queryClient = useQueryClient();
  const [balance, setBalance] = useState(user?.credits || 0);
  const [isLoading, setIsLoading] = useState(false);

  // Update balance when user changes
  useEffect(() => {
    if (user) {
      setBalance(user.credits || 0);
    }
  }, [user]);

  // Stable refreshBalance callback - never changes reference
  const refreshBalance = useCallback(async () => {
    setIsLoading(true);
    try {
      // Trigger a refetch of user data
      await queryClient.invalidateQueries({ queryKey: ['current-user'] });
      await queryClient.invalidateQueries({ queryKey: ['balance'] });
      await queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
    } finally {
      setIsLoading(false);
    }
  }, [queryClient]);

  // Subscribe to WebSocket for real-time balance updates
  const { subscribe } = useGlobalWebSocket();

  useEffect(() => {
    if (!isAuthenticated) return;

    const unsubscribe = subscribe((data) => {
      switch (data.type) {
        case 'balance_update':
          setBalance(data.new_balance);
          // Update user in auth context
          updateUserBalance(data.new_balance);
          // Also update query cache for consistency
          queryClient.setQueryData(['current-user'], (oldData: any) => {
            if (oldData) {
              return {
                ...oldData,
                credits: data.new_balance
              };
            }
            return oldData;
          });
          break;

        case 'subscription_updated':
          // Trigger a full refresh of user data to get new subscription status
          void refreshBalance();
          toast({
            title: "Abo aktualisiert!",
            description: `Dein Abo "${data.plan_name}" ist jetzt aktiv.`,
          });
          break;

        case 'transaction_completed':
          setBalance(data.new_balance);
          // Update user in auth context
          updateUserBalance(data.new_balance);
          // Invalidate transaction queries
          void queryClient.invalidateQueries({ queryKey: ['transactions'] });

          // Show notification for purchases
          if (data.transaction_type === 'purchase') {
            toast({
              title: "Coins erhalten!",
              description: `${data.credits_amount} Coins wurden deinem Konto gutgeschrieben.`,
            });
          }
          break;

        case 'insufficient_funds':
          toast({
            title: "Nicht genug Guthaben",
            description: `Du benötigst ${data.required_amount} Coins. Aktuelles Guthaben: ${data.current_balance} Coins.`,
            variant: "destructive",
          });
          break;
      }
    });

    return () => { if (unsubscribe) { unsubscribe(); } };
  }, [isAuthenticated, subscribe, updateUserBalance, queryClient, refreshBalance]);

  // Memoize balance state value
  const balanceValue = useMemo<WalletBalanceContextType>(() => ({
    balance,
    isLoading,
  }), [balance, isLoading]);

  // Memoize actions value - stable reference via useCallback
  const actionsValue = useMemo<WalletActionsContextType>(() => ({
    refreshBalance,
  }), [refreshBalance]);

  return (
    <WalletBalanceContext.Provider value={balanceValue}>
      <WalletActionsContext.Provider value={actionsValue}>
        {children}
      </WalletActionsContext.Provider>
    </WalletBalanceContext.Provider>
  );
};
